import React, { useState, useEffect, useCallback } from "react";
import { Lock, LogOut, Package, MessageSquare, TrendingUp, Clock, CheckCircle, Truck, AlertCircle, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import BlogEditor from "../components/BlogEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = [
  { value: "pending_payment", label: "Pending Payment", color: "bg-yellow-100 text-yellow-800" },
  { value: "paid", label: "Paid", color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "In Progress", color: "bg-purple-100 text-purple-800" },
  { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" },
  { value: "delivered", label: "Delivered", color: "bg-emerald-100 text-emerald-800" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800" },
];

const ENQUIRY_STATUSES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "responded", label: "Responded", color: "bg-green-100 text-green-800" },
  { value: "closed", label: "Closed", color: "bg-gray-100 text-gray-600" },
];

function getStatusBadge(status, statusList) {
  const s = statusList.find((st) => st.value === status);
  if (!s) return <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 font-medium">{status}</span>;
  return <span className={`text-xs px-2 py-1 ${s.color} font-medium`}>{s.label}</span>;
}

function formatDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// --- Login Component ---
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      toast.error("Invalid password");
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-[#FAFAF8] pt-20">
      <div className="w-full max-w-sm mx-auto px-6">
        <div className="bg-white border border-[#E8E8E4] p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0D2D54] text-white mb-4">
              <Lock size={20} />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">Admin Access</h1>
            <p className="text-sm text-[#6B7280] mt-2">Enter your admin password to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full border border-[#E8E8E4] bg-white px-4 py-3 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
              autoFocus
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-[#0D2D54] text-white py-3 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// --- Status Dropdown ---
function StatusDropdown({ currentStatus, statuses, onUpdate, testId }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-xs hover:bg-[#FAFAF8] px-2 py-1 border border-[#E8E8E4] transition-colors"
      >
        {getStatusBadge(currentStatus, statuses)}
        <ChevronDown size={12} className="text-[#6B7280]" />
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-[#E8E8E4] shadow-lg min-w-[160px]">
          {statuses.map((s) => (
            <button
              key={s.value}
              data-testid={`status-option-${s.value}`}
              onClick={() => { onUpdate(s.value); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-xs hover:bg-[#FAFAF8] transition-colors ${
                s.value === currentStatus ? "bg-[#FAFAF8] font-medium" : ""
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Dashboard Component ---
function Dashboard({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, enquiriesRes, blogRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/blog/posts`, { headers }),
        axios.get(`${API}/admin/invoices`, { headers }),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setEnquiries(enquiriesRes.data);
      setBlogPosts(blogRes.data);
      setInvoices(invoicesRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load data");
      }
    }
    setLoading(false);
  }, [token, onLogout]); // eslint-disable-line react-hooks/exhaustive-deps -- headers derived from token

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status }, { headers });
      toast.success(`Order updated to ${status}`);
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status } : o));
      fetchData();
    } catch {
      toast.error("Failed to update order");
    }
  };

  const updateEnquiryStatus = async (enquiryId, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${enquiryId}/status`, { status }, { headers });
      toast.success(`Enquiry updated to ${status}`);
      setEnquiries((prev) => prev.map((e) => e.id === enquiryId ? { ...e, status } : e));
      fetchData();
    } catch {
      toast.error("Failed to update enquiry");
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, { headers });
      toast.success("Post deleted");
      setBlogPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const savePost = async (postData) => {
    try {
      if (editingPost) {
        await axios.patch(`${API}/admin/blog/posts/${editingPost.id}`, postData, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog/posts`, postData, { headers });
        toast.success("Post created");
      }
      setShowPostForm(false);
      setEditingPost(null);
      fetchData();
    } catch {
      toast.error("Failed to save post");
    }
  };

  const createInvoice = async (invoiceData) => {
    try {
      const res = await axios.post(`${API}/admin/invoices`, invoiceData, { headers });
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setShowInvoiceForm(false);
      fetchData();
    } catch {
      toast.error("Failed to create invoice");
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${API}/admin/invoices/${invoiceId}/status`, { status }, { headers });
      toast.success(`Invoice updated to ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update invoice");
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <p className="text-sm text-[#6B7280]">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAF8] pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-medium text-[#0D2D54]">Admin Dashboard</h1>
            <p className="text-sm text-[#6B7280] mt-1">Manage orders and enquiries</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0D2D54] border border-[#E8E8E4] px-4 py-2 hover:bg-white transition-all"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#E8E8E4] p-5">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp size={16} className="text-[#758C7B]" />
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B7280]">Revenue</span>
              </div>
              <p data-testid="stat-revenue" className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">
                &pound;{(stats.total_revenue || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white border border-[#E8E8E4] p-5">
              <div className="flex items-center gap-3 mb-2">
                <Package size={16} className="text-[#0D2D54]" />
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B7280]">Total Orders</span>
              </div>
              <p data-testid="stat-total-orders" className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">
                {stats.total_orders}
              </p>
            </div>
            <div className="bg-white border border-[#E8E8E4] p-5">
              <div className="flex items-center gap-3 mb-2">
                <Clock size={16} className="text-yellow-500" />
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B7280]">Pending</span>
              </div>
              <p data-testid="stat-pending" className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">
                {stats.pending_orders + stats.paid_orders}
              </p>
            </div>
            <div className="bg-white border border-[#E8E8E4] p-5">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare size={16} className="text-blue-500" />
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B7280]">New Enquiries</span>
              </div>
              <p data-testid="stat-new-enquiries" className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">
                {stats.new_enquiries}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-[#E8E8E4]">
          <button
            data-testid="tab-orders"
            onClick={() => setActiveTab("orders")}
            className={`px-6 py-3 text-sm font-medium tracking-wide border-b-2 transition-colors ${
              activeTab === "orders"
                ? "border-[#0D2D54] text-[#0D2D54]"
                : "border-transparent text-[#6B7280] hover:text-[#0D2D54]"
            }`}
          >
            Orders ({orders.length})
          </button>
          <button
            data-testid="tab-enquiries"
            onClick={() => setActiveTab("enquiries")}
            className={`px-6 py-3 text-sm font-medium tracking-wide border-b-2 transition-colors ${
              activeTab === "enquiries"
                ? "border-[#0D2D54] text-[#0D2D54]"
                : "border-transparent text-[#6B7280] hover:text-[#0D2D54]"
            }`}
          >
            Enquiries ({enquiries.length})
          </button>
          <button
            data-testid="tab-blog"
            onClick={() => setActiveTab("blog")}
            className={`px-6 py-3 text-sm font-medium tracking-wide border-b-2 transition-colors ${
              activeTab === "blog"
                ? "border-[#0D2D54] text-[#0D2D54]"
                : "border-transparent text-[#6B7280] hover:text-[#0D2D54]"
            }`}
          >
            Blog ({blogPosts.length})
          </button>
          <button
            data-testid="tab-invoices"
            onClick={() => setActiveTab("invoices")}
            className={`px-6 py-3 text-sm font-medium tracking-wide border-b-2 transition-colors ${
              activeTab === "invoices"
                ? "border-[#0D2D54] text-[#0D2D54]"
                : "border-transparent text-[#6B7280] hover:text-[#0D2D54]"
            }`}
          >
            Invoices ({invoices.length})
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div data-testid="orders-table" className="bg-white border border-[#E8E8E4] overflow-x-auto">
            {orders.length === 0 ? (
              <div className="p-12 text-center">
                <Package size={32} className="mx-auto mb-3 text-[#E8E8E4]" />
                <p className="text-sm text-[#6B7280]">No orders yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Order</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Customer</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Images</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Total</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Status</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, i) => (
                    <tr key={order.id} data-testid={`order-row-${i}`} className="border-b border-[#E8E8E4] hover:bg-[#FAFAF8]/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-xs font-mono text-[#0D2D54]">{order.id?.slice(0, 8)}...</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-[#0D2D54]">{order.customer_name}</p>
                        <p className="text-xs text-[#6B7280]">{order.customer_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#0D2D54]">{order.quantity}</span>
                        <span className="text-xs text-[#6B7280] ml-1">@ &pound;{order.unit_price}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-[#0D2D54]">&pound;{(order.total || 0).toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusDropdown
                          currentStatus={order.status}
                          statuses={ORDER_STATUSES}
                          onUpdate={(status) => updateOrderStatus(order.id, status)}
                          testId={`order-status-dropdown-${i}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-[#6B7280]">{formatDate(order.created_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Enquiries Tab */}
        {activeTab === "enquiries" && (
          <div data-testid="enquiries-table" className="bg-white border border-[#E8E8E4] overflow-x-auto">
            {enquiries.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare size={32} className="mx-auto mb-3 text-[#E8E8E4]" />
                <p className="text-sm text-[#6B7280]">No enquiries yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Name</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Service</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Message</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Status</th>
                    <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enq, i) => (
                    <tr key={enq.id} data-testid={`enquiry-row-${i}`} className="border-b border-[#E8E8E4] hover:bg-[#FAFAF8]/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-[#0D2D54]">{enq.name}</p>
                        <p className="text-xs text-[#6B7280]">{enq.email}</p>
                        {enq.phone && <p className="text-xs text-[#6B7280]">{enq.phone}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-[#FAFAF8] text-[#0D2D54] font-medium">
                          {enq.service_type === "3d_renders" ? "3D Renders" : enq.service_type === "virtual_staging" ? "Virtual Staging" : "General"}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-sm text-[#6B7280] truncate">{enq.message}</p>
                      </td>
                      <td className="px-4 py-3">
                        <StatusDropdown
                          currentStatus={enq.status}
                          statuses={ENQUIRY_STATUSES}
                          onUpdate={(status) => updateEnquiryStatus(enq.id, status)}
                          testId={`enquiry-status-dropdown-${i}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-[#6B7280]">{formatDate(enq.created_at)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === "blog" && (
          <div data-testid="blog-management">
            {showPostForm ? (
              <BlogEditor
                post={editingPost}
                blogPosts={blogPosts}
                token={token}
                onSave={savePost}
                onCancel={() => { setShowPostForm(false); setEditingPost(null); }}
              />
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-[#0D2D54]">All Blog Posts</h3>
                  <button
                    data-testid="new-post-btn"
                    onClick={() => { setEditingPost(null); setShowPostForm(true); }}
                    className="bg-[#0D2D54] text-white px-4 py-2 text-xs font-medium hover:bg-[#1A3D6B] transition-all"
                  >
                    + New Post
                  </button>
                </div>
                <div className="bg-white border border-[#E8E8E4] overflow-x-auto">
                  {blogPosts.length === 0 ? (
                    <div className="p-12 text-center">
                      <p className="text-sm text-[#6B7280]">No blog posts yet</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Title</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Category</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Status</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Date</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blogPosts.map((post, i) => (
                          <tr key={post.id} data-testid={`blog-row-${i}`} className="border-b border-[#E8E8E4] hover:bg-[#FAFAF8]/50">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-[#0D2D54]">{post.title}</p>
                              <p className="text-xs text-[#6B7280]">/blog/{post.slug}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs bg-[#FAFAF8] text-[#0D2D54] px-2 py-1">{post.category}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-1 ${post.published ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                                {post.published ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-[#6B7280]">{formatDate(post.created_at)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  data-testid={`edit-post-${i}`}
                                  onClick={() => { setEditingPost(post); setShowPostForm(true); }}
                                  className="text-xs text-[#0D2D54] hover:underline"
                                >
                                  Edit
                                </button>
                                <button
                                  data-testid={`delete-post-${i}`}
                                  onClick={() => deletePost(post.id)}
                                  className="text-xs text-red-500 hover:underline"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div data-testid="invoices-management">
            {showInvoiceForm ? (
              <InvoiceForm onSave={createInvoice} onCancel={() => setShowInvoiceForm(false)} />
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-[#0D2D54]">All Invoices</h3>
                  <button
                    data-testid="new-invoice-btn"
                    onClick={() => setShowInvoiceForm(true)}
                    className="bg-[#0D2D54] text-white px-4 py-2 text-xs font-medium hover:bg-[#1A3D6B] transition-all"
                  >
                    + New Invoice
                  </button>
                </div>
                <div className="bg-white border border-[#E8E8E4] overflow-x-auto">
                  {invoices.length === 0 ? (
                    <div className="p-12 text-center">
                      <p className="text-sm text-[#6B7280]">No invoices yet</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Invoice</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Customer</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Total</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Method</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Status</th>
                          <th className="text-left text-xs uppercase tracking-[0.1em] text-[#6B7280] px-4 py-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv, i) => (
                          <tr key={inv.id} data-testid={`invoice-row-${i}`} className="border-b border-[#E8E8E4] hover:bg-[#FAFAF8]/50">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-[#0D2D54]">{inv.invoice_number}</p>
                              <p className="text-xs text-[#6B7280]">{formatDate(inv.created_at)}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-[#0D2D54]">{inv.customer_name}</p>
                              <p className="text-xs text-[#6B7280]">{inv.customer_email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-[#0D2D54]">&pound;{Number(inv.total).toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs px-2 py-1 bg-[#FAFAF8] text-[#0D2D54]">
                                {inv.payment_method === "bank_transfer" ? "Bank Transfer" : "Card"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <StatusDropdown
                                currentStatus={inv.payment_status}
                                statuses={[
                                  { value: "unpaid", label: "Unpaid", color: "bg-yellow-100 text-yellow-800" },
                                  { value: "paid", label: "Paid", color: "bg-green-100 text-green-800" },
                                  { value: "overdue", label: "Overdue", color: "bg-red-100 text-red-800" },
                                  { value: "cancelled", label: "Cancelled", color: "bg-gray-100 text-gray-600" },
                                ]}
                                onUpdate={(status) => updateInvoiceStatus(inv.id, status)}
                                testId={`invoice-status-${i}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <a
                                href={`/invoice/${inv.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#0D2D54] hover:underline"
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function BlogPostForm({ post, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    featured_image: post?.featured_image || "",
    category: post?.category || "Virtual Staging",
    tags: post?.tags?.join(", ") || "",
    author: post?.author || "The Secret Seller",
    published: post?.published ?? true,
  });
  const [uploading, setUploading] = useState(false);
  const contentRef = React.useRef(null);

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm({ ...form, title, slug: post ? form.slug : generateSlug(title) });
  };

  // Insert text at cursor position in content textarea
  const insertAtCursor = (before, after = "") => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.substring(start, end);
    const newContent = form.content.substring(0, start) + before + selected + after + form.content.substring(end);
    setForm({ ...form, content: newContent });
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = start + before.length + selected.length;
    }, 10);
  };

  const handleBold = () => insertAtCursor("**", "**");
  const handleH1 = () => insertAtCursor("\n\n## ", "\n\n");
  const handleH2 = () => insertAtCursor("\n\n### ", "\n\n");
  const handleBullet = () => insertAtCursor("\n- ");

  // Upload featured image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      const imageUrl = `${API}/files/${res.data.storage_path}`;
      setForm({ ...form, featured_image: imageUrl });
      toast.success("Image uploaded");
    } catch {
      toast.error("Failed to upload image");
    }
    setUploading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.slug || !form.content) {
      toast.error("Title, slug and content are required");
      return;
    }
    onSave({
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
  };

  return (
    <div data-testid="blog-post-form" className="bg-white border border-[#E8E8E4] p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">
          {post ? "Edit Post" : "New Post"}
        </h3>
        <button onClick={onCancel} className="text-xs text-[#6B7280] hover:text-[#0D2D54]">Cancel</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Title *</label>
          <input
            type="text"
            data-testid="post-title-input"
            value={form.title}
            onChange={handleTitleChange}
            className="w-full border border-[#E8E8E4] px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
            placeholder="Post title"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Slug *</label>
          <input
            type="text"
            data-testid="post-slug-input"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="w-full border border-[#E8E8E4] px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
            placeholder="post-url-slug"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Excerpt</label>
          <textarea
            data-testid="post-excerpt-input"
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            rows={2}
            className="w-full border border-[#E8E8E4] px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none resize-none"
            placeholder="Brief summary for blog listing and SEO description"
          />
        </div>

        {/* Content editor with toolbar */}
        <div>
          <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Content *</label>
          <div className="border border-[#E8E8E4] focus-within:border-[#0D2D54] transition-colors">
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-2 py-2 bg-[#FAFAF8] border-b border-[#E8E8E4]">
              <button
                type="button"
                data-testid="toolbar-bold"
                onClick={handleBold}
                className="px-3 py-1.5 text-xs font-bold text-[#0D2D54] hover:bg-white border border-transparent hover:border-[#E8E8E4] transition-all"
                title="Bold"
              >
                B
              </button>
              <button
                type="button"
                data-testid="toolbar-h1"
                onClick={handleH1}
                className="px-3 py-1.5 text-xs font-bold text-[#0D2D54] hover:bg-white border border-transparent hover:border-[#E8E8E4] transition-all"
                title="Heading 1"
              >
                H1
              </button>
              <button
                type="button"
                data-testid="toolbar-h2"
                onClick={handleH2}
                className="px-3 py-1.5 text-xs font-bold text-[#0D2D54] hover:bg-white border border-transparent hover:border-[#E8E8E4] transition-all"
                title="Heading 2"
              >
                H2
              </button>
              <button
                type="button"
                data-testid="toolbar-bullet"
                onClick={handleBullet}
                className="px-3 py-1.5 text-xs text-[#0D2D54] hover:bg-white border border-transparent hover:border-[#E8E8E4] transition-all"
                title="Bullet Point"
              >
                &bull; List
              </button>
              <div className="w-px h-5 bg-[#E8E8E4] mx-1" />
              <span className="text-[10px] text-[#6B7280] ml-1">Select text then click to format</span>
            </div>
            {/* Textarea */}
            <textarea
              ref={contentRef}
              data-testid="post-content-input"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={16}
              className="w-full px-3 py-3 text-sm text-[#0D2D54] focus:outline-none resize-y font-mono leading-relaxed"
              placeholder="Write your post content here..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Category</label>
            <select
              data-testid="post-category-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-[#E8E8E4] px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
            >
              <option value="Virtual Staging">Virtual Staging</option>
              <option value="Property Marketing">Property Marketing</option>
              <option value="3D Renders">3D Renders</option>
              <option value="Industry News">Industry News</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Featured Image</label>
            <div className="flex gap-2">
              <label className="flex-1 border border-[#E8E8E4] px-3 py-2 text-xs text-[#6B7280] cursor-pointer hover:bg-[#FAFAF8] transition-colors flex items-center justify-center gap-1">
                {uploading ? "Uploading..." : form.featured_image ? "Change Image" : "Upload Image"}
                <input
                  type="file"
                  data-testid="post-image-upload"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
            </div>
            {form.featured_image && (
              <div className="mt-2 relative">
                <img src={form.featured_image} alt="Preview" className="w-full h-20 object-cover border border-[#E8E8E4]" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, featured_image: "" })}
                  className="absolute top-1 right-1 bg-white text-red-500 text-xs px-1.5 py-0.5 border shadow-sm"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-1">Tags (comma separated)</label>
            <input
              type="text"
              data-testid="post-tags-input"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full border border-[#E8E8E4] px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none"
              placeholder="virtual staging, property, UK"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="post-published-input"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-[#0D2D54]">Published</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            data-testid="save-post-btn"
            className="bg-[#0D2D54] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#1A3D6B] transition-all"
          >
            {post ? "Update Post" : "Publish Post"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="border border-[#E8E8E4] text-[#6B7280] px-6 py-2.5 text-sm hover:bg-[#FAFAF8] transition-all"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// --- Invoice Form ---
function InvoiceForm({ onSave, onCancel }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    project_name: "",
    payment_method: "bank_transfer",
    notes: "",
    items: [{ description: "Virtual Staging", quantity: 1, unit_price: 65 }],
  });

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: field === "quantity" || field === "unit_price" ? Number(value) : value };
    setForm({ ...form, items });
  };

  const total = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || form.items.length === 0) {
      toast.error("Name, email and at least one item required");
      return;
    }
    onSave(form);
  };

  return (
    <div data-testid="invoice-form" className="bg-white border border-[#E8E8E4] p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">New Invoice</h3>
        <button onClick={onCancel} className="text-xs text-[#6B7280] hover:text-[#0D2D54]">Cancel</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Customer Name *</label>
            <input type="text" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="w-full border border-[#E8E8E4] px-3 py-2 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none" placeholder="Client name" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Customer Email *</label>
            <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              className="w-full border border-[#E8E8E4] px-3 py-2 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none" placeholder="client@email.com" />
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Project Name</label>
          <input type="text" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })}
            className="w-full border border-[#E8E8E4] px-3 py-2 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none" placeholder="e.g. 42 Manchester Road, Virtual Staging" />
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Payment Method</label>
          <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            className="w-full border border-[#E8E8E4] px-3 py-2 text-sm text-[#0D2D54] focus:outline-none">
            <option value="bank_transfer">Bank Transfer</option>
            <option value="stripe">Card (Stripe)</option>
          </select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-2">Line Items</label>
          <div className="space-y-2">
            {form.items.map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input type="text" value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)}
                  className="col-span-6 border border-[#E8E8E4] px-2 py-1.5 text-xs focus:outline-none" placeholder="Description" />
                <input type="number" value={item.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)}
                  className="col-span-2 border border-[#E8E8E4] px-2 py-1.5 text-xs text-center focus:outline-none" min="1" />
                <div className="col-span-3 flex items-center border border-[#E8E8E4]">
                  <span className="text-xs text-[#6B7280] px-1">&pound;</span>
                  <input type="number" value={item.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                    className="w-full px-1 py-1.5 text-xs focus:outline-none" step="0.01" />
                </div>
                <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-xs text-red-500 hover:text-red-700 text-center">&times;</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addItem} className="text-xs text-[#0D2D54] hover:underline mt-2">+ Add item</button>
        </div>

        <div className="flex justify-end py-2 border-t border-[#E8E8E4]">
          <span className="text-sm font-medium text-[#0D2D54]">Total: &pound;{total.toFixed(2)}</span>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Notes (optional)</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2} className="w-full border border-[#E8E8E4] px-3 py-2 text-sm focus:outline-none resize-none" placeholder="Additional notes..." />
        </div>

        <div className="flex gap-3">
          <button type="submit" className="bg-[#0D2D54] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#1A3D6B] transition-all">
            Create Invoice
          </button>
          <button type="button" onClick={onCancel} className="border border-[#E8E8E4] text-[#6B7280] px-6 py-2.5 text-sm hover:bg-[#FAFAF8]">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// --- Main Admin Page ---
export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));

  const handleLogin = (t) => {
    sessionStorage.setItem("admin_token", t);
    setToken(t);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}
